// @flow

import React, { useMemo } from 'react';
import PlayingCard from '../PlayingCard';
import styles from './styles.module.scss';
import { sortCards } from '../../utils/game/sortRandomCards';
import cn from 'classnames';

type Props = {
  actionRight: string,
  highlight: boolean,
  combo: Array<string>,
  actions: {
    call?: { ev: number, weight: number, easy_fold: boolean },
    check?: { ev: number, weight: number, easy_fold: boolean },
    fold?: { ev: number, weight: number, easy_fold: boolean },
    raise?: { ev: number, weight: number, easy_fold: boolean },
  },
  limit: string,
  stackSize: string,
  isEasyRFI: boolean,
  isPlo5: boolean,
  top?: number,
  percentsResolver?: string,
}

const GameResult = ({
  combo, actions, actionRight, highlight, limit, stackSize,
  isEasyRFI, isPlo5, top, percentsResolver,
}: Props) => {
  const handleShow = (type: string) => (
    ((actions?.[type]?.ev?.toString()?.replace('\\/r', '') / 2000).toFixed(3) > 0)
    || (Math.round(actions?.[type]?.weight.toFixed(1)) > 0)
  );

  const handleCheckCorrectness = (type: string) => actionRight?.indexOf(type) + 1;

  const exceptions = [
    'high 50bb',
    'high 30bb',
  ];

  const isException = exceptions.includes(`${limit} ${stackSize}`);

  const handleValue = (type: string, weight: number) => {
    if (type === 'raise' && percentsResolver === 'hands') {
      return (
        actions?.[type] ? `${
          (actions?.[type]?.ev?.toString()?.replace('\\/r', '') / 2000).toFixed(3)
        }(${top}%)` : ''
      );
    }
    if (type === 'fold' && percentsResolver === 'hands') {
      return (
        actions?.[type] ? `${
          (actions?.[type]?.ev?.toString()?.replace('\\/r', '') / 2000).toFixed(3)
        }` : ''
      );
    }
    return (
      actions?.[type] ? `${
        (actions?.[type]?.ev?.toString()?.replace('\\/r', '') / 2000).toFixed(3)
      }(${weight?.toFixed(1)}%)` : ''
    );
  };
  // eslint-disable-next-line no-underscore-dangle
  const _combo = sortCards(combo, isPlo5).map((e) => [e[0], e[1]]);

  const convertSingleResult = (type: string, weight: number) => (
    actions?.[type] ? `${
      (actions?.[type]?.ev?.toString()?.replace('\\/r', '') / 2000).toFixed(3)
    }(${parseInt(weight.toFixed(0), 10).toFixed(1)}%)` : ''
  );

  const data = useMemo(() => [
    {
      name: 'Raise',
      type: 'raise',
      ev: actions?.raise?.ev || '',
      weight: actions?.raise?.weight || '',
      easyFold: actions?.raise?.easy_fold || false,
    },
    {
      name: 'Fold',
      type: 'fold',
      ev: actions?.fold?.ev || '',
      weight: actions?.fold?.weight || '',
      easyFold: actions?.fold?.easy_fold || false,
    },
    {
      name: 'Call',
      type: 'call',
      ev: actions?.call?.ev || '',
      weight: actions?.call?.weight || '',
      easyFold: actions?.call?.easy_fold || false,
    },
    {
      name: 'Check',
      type: 'check',
      ev: actions?.check?.ev || '',
      weight: actions?.check?.weight || '',
      easyFold: actions?.check?.easy_fold || false,
    },
  ].sort((a, b) => b.weight - a.weight)
    .filter((el) => handleShow(el.type))
    .filter((el) => el.weight > 0.3), []);

  // const dataToShow = data.filter((el) => handleShow(el.type)).filter((el) => el.weight > 0.3);
  const sum = data.reduce((s, { weight }) => s + weight, 0);
  data[0].weight += (100 - sum);

  return (
    <div
      className={cn(styles.resultWrapper, { [styles.highlight]: highlight })}
    >
      <div className={styles.resultCards}>
        {_combo.map((card) => (
          <PlayingCard size="xs" key={card} suit={card[1]} value={card[0]} noSuit />
        ))}
        <div className={styles.resultText}>
          {
            data.map((e) => (
              <div
                className={cn(styles.resultTextXS,
                  { [styles.right]: handleCheckCorrectness(e.type) })}
                key={e.type}
              >
                {
                  (isEasyRFI && e.easyFold && e.type === 'fold')
                    ? <div className={cn(styles.resultName, styles.resultNameFold)}>Easy Fold</div>
                    : <div className={cn(styles.resultName, styles[`resultName${e.name}`])}>{e.name}</div>
                }
                <div className={styles.resultTextEV}>
                  {
                    data.length === 1 && isException
                      ? convertSingleResult(e.type, e.weight) : handleValue(e.type, e.weight)
                  }
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default GameResult;
